/**
 * VendorActivityFeed
 * ─────────────────────────────────────────────────────────────────────────────
 * Vendor's chronological, filterable, drillable log of every change made to
 * their shop, by anyone (staff, owner, or Lusuku Support).
 *
 * Phase 0 surface:
 *   - Filter chips (domain)
 *   - Date range picker (from / to)
 *   - Paginated list (25 per page) with infinite-scroll "Load more"
 *   - Row detail drawer showing before/after/diff + reason + actor
 *   - Inline HelpTooltip on the header and each filter group
 *   - Empty state for brand-new vendors with welcoming explainer
 *
 * Ownership: backend already scopes rows to `req.userId` unless caller is an
 * admin (in which case `?vendorId=` applies). No client-side auth checks
 * needed here beyond normal login.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Modal,
  Alert,
} from "react-bootstrap";
import moment from "moment";
import {
  apiGetVendorActivity,
  apiGetActivityRow,
  apiGetAuditMeta,
} from "../api/index";
import HelpTooltip from "../components/HelpTooltip";

// ─── Display labels ────────────────────────────────────────────────────────
// Mirror constants/auditDomains.js on the server. Keep labels human-friendly.

const DOMAIN_LABELS = {
  PRODUCT: { label: "Products", icon: "fa-box", colour: "#2d7a5f" },
  PRICING: { label: "Pricing", icon: "fa-tags", colour: "#b8860b" },
  HOURS: { label: "Hours", icon: "fa-clock", colour: "#385f9b" },
  STAFF: { label: "Staff", icon: "fa-user-tie", colour: "#8e44ad" },
  MEDIA: { label: "Media", icon: "fa-image", colour: "#16a085" },
  PROFILE: { label: "Profile", icon: "fa-store", colour: "#6c757d" },
  DELIVERY_CONFIG: { label: "Delivery", icon: "fa-motorcycle", colour: "#d35400" },
  CS_INTERVENTION: { label: "Lusuku Support", icon: "fa-headset", colour: "#c0392b" },
  STOCK: { label: "Stock", icon: "fa-warehouse", colour: "#27ae60" },
  ORDER: { label: "Orders", icon: "fa-shopping-bag", colour: "#2980b9" },
  FINANCIAL: { label: "Wallet", icon: "fa-coins", colour: "#8b5a2b" },
  SESSION: { label: "Logins", icon: "fa-sign-in-alt", colour: "#34495e" },
};

const ACTION_LABELS = {
  PRODUCT_CREATE: "Product created",
  PRODUCT_RENAME: "Product renamed",
  PRODUCT_DESCRIPTION_EDIT: "Description edited",
  PRODUCT_IMAGE_ADD: "Image added",
  PRODUCT_IMAGE_REMOVE: "Image removed",
  PRODUCT_IMAGE_REORDER: "Images reordered",
  PRODUCT_CATEGORY_CHANGE: "Category changed",
  PRODUCT_ACTIVATE: "Product activated",
  PRODUCT_DEACTIVATE: "Product deactivated",
  PRODUCT_ARCHIVE: "Product archived",
  PRODUCT_STOCK_THRESHOLD_CHANGE: "Stock threshold changed",
  PRICE_CHANGE: "Price changed",
  COMMISSION_RATE_CHANGE: "Commission rate changed",
  DISCOUNT_APPLY: "Discount applied",
  DISCOUNT_REMOVE: "Discount removed",
  PROMO_CREATE: "Promotion started",
  PROMO_END: "Promotion ended",
  HOURS_CHANGE: "Opening hours changed",
  OUTLET_OPEN: "Outlet opened",
  OUTLET_CLOSE: "Outlet closed",
  HOLIDAY_OVERRIDE_SET: "Holiday override set",
  UNAVAILABLE_FLAG_SET: "Marked unavailable",
  UNAVAILABLE_FLAG_CLEAR: "Availability restored",
  STAFF_INVITE: "Staff invited",
  STAFF_ACCEPT: "Staff joined",
  STAFF_REMOVE: "Staff removed",
  STAFF_ROLE_CHANGE: "Staff role changed",
  STAFF_PERMISSION_CHANGE: "Staff permissions changed",
  VENDOR_CREDENTIALS_SENT: "Sign-in details emailed by Lusuku Support",
  VENDOR_LOGIN_SET: "Sign-in details set by Lusuku Support",
  TWO_FA_ENABLE: "2-factor auth enabled",
  TWO_FA_DISABLE: "2-factor auth disabled",
  LOGIN_SUCCESS: "Logged in",
  LOGIN_FAILURE: "Login failed",
  LOGOUT: "Logged out",
  SESSION_EXPIRED: "Session expired",
  CONCURRENT_SESSION_DETECTED: "Duplicate session detected",
  MEDIA_UPLOAD: "Media uploaded",
  MEDIA_ENHANCE: "Media enhanced",
  MEDIA_REPLACE: "Media replaced",
  MEDIA_ARCHIVE: "Media archived",
  SHOOT_SESSION_ISSUED: "Photo session link issued",
  SHOOT_SESSION_EXPIRED: "Photo session expired",
  BANK_DETAILS_CHANGE: "Bank details changed",
  RADIUS_CHANGE: "Delivery radius changed",
  LOCATION_CHANGE: "Location changed",
  FEATURED_IMAGE_CHANGE: "Featured image changed",
  TAGLINE_CHANGE: "Tagline changed",
  DEACTIVATION: "Account deactivated",
  REACTIVATION: "Account reactivated",
  ADMIN_PRODUCT_EDIT: "Product edited by Lusuku Support",
  ADMIN_HOURS_OVERRIDE: "Hours overridden by Lusuku Support",
  ADMIN_OUTLET_TOGGLE: "Outlet toggled by Lusuku Support",
  ADMIN_REFUND_ISSUED: "Refund issued by Lusuku Support",
  ADMIN_ORDER_CANCELLED: "Order cancelled by Lusuku Support",
  ADMIN_RIDER_REASSIGNED: "Rider reassigned by Lusuku Support",
  ADMIN_WALLET_ADJUSTMENT: "Wallet adjusted by Lusuku Support",
  SUPPORT_CASE_OPENED: "Support case opened",
  SUPPORT_CASE_STATUS_CHANGE: "Support case status changed",
  SUPPORT_CASE_ASSIGNED: "Support case assigned",
  SUPPORT_CASE_EVIDENCE_ADDED: "Evidence added to a support case",
  SUPPORT_CASE_ESCALATED: "Support case escalated",
  SUPPORT_CASE_RESOLVED: "Support case resolved",
  SUPPORT_CASE_REOPENED: "Support case reopened",
  SUPPORT_CASE_CANCELLED: "Support case cancelled",
};

const ACTOR_LABELS = {
  OWNER: { label: "Owner", colour: "#2d7a5f" },
  VENDOR_STAFF: { label: "Staff", colour: "#8e44ad" },
  SUPERVISOR: { label: "Supervisor", colour: "#ff9800" },
  ADMIN: { label: "Lusuku Support", colour: "#c0392b" },
  SYSTEM: { label: "System", colour: "#607d8b" },
  CUSTOMER: { label: "Customer", colour: "#2980b9" },
  RIDER: { label: "Rider", colour: "#d35400" },
  PARTNER_3PL: { label: "3PL Partner", colour: "#d35400" },
};

const PAGE_SIZE = 25;

// ─── Main component ────────────────────────────────────────────────────────

function VendorActivityFeed() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [domainFilter, setDomainFilter] = useState(null); // null = all
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [meta, setMeta] = useState({ domains: [], actions: [], actorTypes: [] });

  // Load enum meta once (cacheable, no auth needed)
  useEffect(() => {
    apiGetAuditMeta()
      .then((r) => setMeta(r.data?.data || {}))
      .catch(() => { /* non-critical */ });
  }, []);

  const buildParams = useCallback(
    (pageNumber) => {
      const params = { page: pageNumber, limit: PAGE_SIZE };
      if (domainFilter) params.domain = domainFilter;
      if (fromDate) params.from = moment(fromDate).startOf("day").toISOString();
      if (toDate) params.to = moment(toDate).endOf("day").toISOString();
      return params;
    },
    [domainFilter, fromDate, toDate]
  );

  const loadPage = useCallback(
    async (pageNumber, append = false) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const resp = await apiGetVendorActivity(buildParams(pageNumber));
        const body = resp.data || {};
        const incoming = Array.isArray(body.data) ? body.data : [];
        setRows((prev) => (append ? [...prev, ...incoming] : incoming));
        setTotal(body.total || 0);
        setHasMore(Boolean(body.hasMore));
        setPage(pageNumber);
      } catch (err) {
        // Backend may 401 on cold start — show friendly message rather than a stack trace
        setError(
          err?.response?.data?.message ||
            "Couldn't load your activity log. Please try again in a moment."
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildParams]
  );

  // Refetch on filter change
  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailLoading(true);
    try {
      const resp = await apiGetActivityRow(row._id);
      const full = resp.data?.data;
      if (full) setDetailRow(full);
    } catch {
      // Keep the summary row we already have
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => setDetailRow(null);

  const availableDomains = useMemo(
    () => meta.domains?.length ? meta.domains : Object.keys(DOMAIN_LABELS),
    [meta]
  );

  return (
    <Container fluid className="mt-4 mb-5">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Row className="mb-3">
        <Col>
          <h3 className="mb-0 d-inline-flex align-items-center">
            Activity log
            <HelpTooltip
              title="What is this?"
              body={
                <>
                  This is your shop's permanent, tamper-evident history. Every
                  time you, your staff, or Lusuku Support make a change —
                  editing a product, changing prices, adjusting opening hours,
                  inviting staff, uploading new photos — the change is
                  recorded here with the <strong>who, what, when, and why</strong>.
                  <br /><br />
                  Rows are never deleted. You can export this log anytime and
                  share it with your accountant, auditor, or bank.
                </>
              }
            />
          </h3>
          <div className="text-muted" style={{ fontSize: "0.85rem" }}>
            {total > 0
              ? `${total.toLocaleString()} event${total === 1 ? "" : "s"} recorded`
              : "Your activity log will fill up as you run your shop."}
          </div>
        </Col>
      </Row>

      {/* ── Filters ────────────────────────────────────────────────── */}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <Row className="align-items-end">
            <Col md="7" className="mb-2 mb-md-0">
              <div className="d-flex align-items-center mb-2">
                <strong style={{ fontSize: "0.85rem" }}>Filter by area</strong>
                <HelpTooltip
                  title="Areas"
                  body="Group events by what part of your shop they affected. Click an area to show only those events; click again to clear."
                />
              </div>
              <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                <DomainChip
                  active={domainFilter === null}
                  label="All"
                  colour="#2c3e50"
                  icon="fa-list"
                  onClick={() => setDomainFilter(null)}
                />
                {availableDomains.map((d) => {
                  const meta = DOMAIN_LABELS[d] || {
                    label: d,
                    colour: "#6c757d",
                    icon: "fa-circle",
                  };
                  return (
                    <DomainChip
                      key={d}
                      active={domainFilter === d}
                      label={meta.label}
                      colour={meta.colour}
                      icon={meta.icon}
                      onClick={() => setDomainFilter(domainFilter === d ? null : d)}
                    />
                  );
                })}
              </div>
            </Col>
            <Col md="5">
              <div className="d-flex align-items-center mb-2">
                <strong style={{ fontSize: "0.85rem" }}>Date range</strong>
                <HelpTooltip
                  title="Date range"
                  body="Leave blank to see everything. Pick a from/to date to narrow down."
                />
              </div>
              <Row>
                <Col xs="6">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    aria-label="From date"
                  />
                </Col>
                <Col xs="6">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    aria-label="To date"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Error banner ───────────────────────────────────────────── */}
      {error && (
        <Alert variant="warning" className="py-2" style={{ fontSize: "0.85rem" }}>
          {error}
        </Alert>
      )}

      {/* ── Feed ───────────────────────────────────────────────────── */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <EmptyState hasFilters={Boolean(domainFilter || fromDate || toDate)} />
          ) : (
            <div>
              {rows.map((row) => (
                <ActivityRow key={row._id} row={row} onClick={() => openDetail(row)} />
              ))}
              {hasMore && (
                <div className="text-center p-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={loadingMore}
                    onClick={() => loadPage(page + 1, true)}
                  >
                    {loadingMore ? <Spinner animation="border" size="sm" /> : "Load more"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Detail modal ───────────────────────────────────────────── */}
      <Modal show={!!detailRow} onHide={closeDetail} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {detailRow ? ACTION_LABELS[detailRow.action] || detailRow.action : ""}
            <HelpTooltip
              title="Event detail"
              body="Full before/after snapshot of the change, the reason it was made, and the identity of the actor. This is the record your bank, accountant, or auditor would rely on."
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading full detail…
            </div>
          ) : detailRow ? (
            <RowDetail row={detailRow} />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetail}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function DomainChip({ label, icon, colour, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn btn-sm"
      style={{
        padding: "4px 10px",
        borderRadius: 18,
        border: active ? `1px solid ${colour}` : "1px solid #dee2e6",
        background: active ? colour : "#ffffff",
        color: active ? "#ffffff" : "#495057",
        fontSize: "0.78rem",
        fontWeight: 500,
        lineHeight: 1.2,
      }}
    >
      <i className={`fas ${icon}`} style={{ marginRight: 5 }} /> {label}
    </button>
  );
}

function EmptyState({ hasFilters }) {
  if (hasFilters) {
    return (
      <div className="text-center py-5 px-4">
        <i className="fas fa-filter" style={{ fontSize: "2rem", color: "#cdd3dc" }} />
        <h5 className="mt-3 mb-1">No events match your filters</h5>
        <p className="text-muted" style={{ fontSize: "0.88rem" }}>
          Try removing a filter or picking a wider date range.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center py-5 px-4">
      <i className="fas fa-feather-alt" style={{ fontSize: "2.2rem", color: "#cdd3dc" }} />
      <h5 className="mt-3 mb-1">Your activity log is empty</h5>
      <p className="text-muted mx-auto" style={{ fontSize: "0.88rem", maxWidth: 480 }}>
        As soon as you (or your staff, or Lusuku Support acting on your behalf)
        make a change to your shop, it will show up here. We keep every record
        forever — so you can always go back and see exactly what happened.
      </p>
    </div>
  );
}

function ActivityRow({ row, onClick }) {
  const dm = DOMAIN_LABELS[row.domain] || { colour: "#6c757d", icon: "fa-circle" };
  const actor = ACTOR_LABELS[row.actorType] || { label: row.actorType, colour: "#6c757d" };
  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      role="button"
      tabIndex={0}
      className="d-flex align-items-start px-3 py-3 border-bottom activity-row"
      style={{ cursor: "pointer", transition: "background 0.1s" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f9fc")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "")}
    >
      <div
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: `${dm.colour}1a`, color: dm.colour,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, marginRight: 12,
        }}
      >
        <i className={`fas ${dm.icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.92rem", fontWeight: 500 }}>
          {ACTION_LABELS[row.action] || row.action}
          {row.entityLabel && (
            <span className="text-muted" style={{ fontWeight: 400 }}> · {row.entityLabel}</span>
          )}
        </div>
        <div className="text-muted" style={{ fontSize: "0.78rem", marginTop: 2 }}>
          <Badge
            pill
            style={{ background: actor.colour, marginRight: 6, fontWeight: 500 }}
          >
            {actor.label}
          </Badge>
          {row.actorName || row.actorEmail || "—"}
          {" · "}
          <span title={moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")}>
            {moment(row.createdAt).fromNow()}
          </span>
          {row.reason && (
            <span style={{ marginLeft: 6, fontStyle: "italic" }}>
              · "{row.reason.length > 60 ? row.reason.slice(0, 60) + "…" : row.reason}"
            </span>
          )}
        </div>
      </div>
      <i className="fas fa-chevron-right text-muted" style={{ fontSize: "0.75rem", marginTop: 10 }} />
    </div>
  );
}

function RowDetail({ row }) {
  const diffEntries = row.diff ? Object.entries(row.diff) : [];
  const actor = ACTOR_LABELS[row.actorType] || { label: row.actorType, colour: "#6c757d" };
  return (
    <div>
      <dl className="row mb-3" style={{ fontSize: "0.88rem" }}>
        <dt className="col-sm-4 text-muted">When</dt>
        <dd className="col-sm-8">{moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")}</dd>

        <dt className="col-sm-4 text-muted">Who</dt>
        <dd className="col-sm-8">
          <Badge pill style={{ background: actor.colour, marginRight: 6 }}>
            {actor.label}
          </Badge>
          {row.actorName || "—"} <span className="text-muted">{row.actorEmail ? `(${row.actorEmail})` : ""}</span>
        </dd>

        <dt className="col-sm-4 text-muted">What</dt>
        <dd className="col-sm-8">{ACTION_LABELS[row.action] || row.action}</dd>

        {row.entityLabel && (<>
          <dt className="col-sm-4 text-muted">Affected</dt>
          <dd className="col-sm-8">{row.entityType} · {row.entityLabel}</dd>
        </>)}

        {row.reason && (<>
          <dt className="col-sm-4 text-muted">Reason</dt>
          <dd className="col-sm-8">{row.reason}</dd>
        </>)}

        {row.ip && (<>
          <dt className="col-sm-4 text-muted">IP address</dt>
          <dd className="col-sm-8" style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{row.ip}</dd>
        </>)}
      </dl>

      {/* Diff pills */}
      {diffEntries.length > 0 && (
        <div className="mb-3">
          <div className="d-flex align-items-center mb-2">
            <strong style={{ fontSize: "0.85rem" }}>What changed</strong>
            <HelpTooltip
              title="Changed fields"
              body="Exactly which fields were modified, with their old and new values. A dash (—) means the field didn't exist before or was cleared."
            />
          </div>
          <div className="border rounded p-2 bg-light">
            {diffEntries.map(([field, change]) => (
              <div key={field} className="d-flex align-items-start py-1" style={{ fontSize: "0.85rem" }}>
                <strong style={{ minWidth: 130, color: "#495057" }}>{field}</strong>
                <div style={{ flex: 1 }}>
                  <span style={{ textDecoration: "line-through", color: "#c0392b", marginRight: 8 }}>
                    {formatVal(change.from)}
                  </span>
                  <i className="fas fa-arrow-right text-muted" style={{ fontSize: "0.7rem" }} />
                  <span style={{ color: "#2d7a5f", marginLeft: 8 }}>
                    {formatVal(change.to)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw snapshots (collapsed by default via <details>) */}
      {(row.before || row.after) && (
        <details style={{ fontSize: "0.8rem" }}>
          <summary style={{ cursor: "pointer" }}>Raw snapshots (advanced)</summary>
          <pre className="bg-light border rounded p-2 mt-2" style={{ maxHeight: 300, overflow: "auto" }}>
            {JSON.stringify({ before: row.before, after: row.after }, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function formatVal(v) {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v).slice(0, 100);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

export default VendorActivityFeed;
